import * as React from "react";
import { Row, Col, FormGroup, FormLabel, Button, Alert, FormCheck } from "react-bootstrap";
import { Column, FormInputGroups, MultipleText } from "../../../../../../react-components/lu-component/src/index";
import { Job } from "../../../../../../server/models/job";
import { List } from "../../../parts/list";
import styled from "styled-components";
import { AccountWithoutPassword } from "../../../../../../server/models/account";
import { Title } from "../../../parts/title";
import Feedback from "react-bootstrap/esm/Feedback";
import { getErrorMessage } from "../../../../lib/error";
import { ErrorObject } from "../../../../../../server/types/error";
import { AlertText } from "../../../parts/font";
import { PurchaseRateExtractionDefinitionNotes } from "../../../parts/purchase-rate-extraction-definition-notes";
import { useDisclosure } from "../../../../hooks/use-disclosure";
import { CurrentProductAlertModal } from "./current-product-alert-modal";

const AlertMessage = styled.div`
	color: red;
`;
const IndentArea = styled.div`
	margin-left: 25px;
`;
const WrapArea = styled.div`
	.col-hasBenchmark,
	.col-hasCurrentProduct {
		margin-left: 10px;
	}
`;

const ItemLabel = styled.div`
	margin-top: 1rem;
`;

export type JobCreatePageProps = {
	errors?: ErrorObject;
	job: Partial<Job>;
	loginAccount: AccountWithoutPassword;
	evaluationItem: string[];
	cantEditMessage?: string;
	hasStrategyGroup: boolean;
	hasCategoryGroup: boolean;
	canDemandForecastNeeded: boolean;
	needCurrentProduct: boolean;
	onChangeJob: (name: keyof Job, value: Job[keyof Job]) => void;
	onChangeEvaluationItem: (items: string[]) => void;
	onSubmit: () => void;
	// question?: SimpleQuestion;
	// onChangeQuestion: (question?: SimpleQuestion) => void;
};
export const JobCreatePage = React.memo((props: JobCreatePageProps) => {
	const {
		job,
		evaluationItem,
		cantEditMessage,
		onChangeJob,
		onChangeEvaluationItem,
		onSubmit,
		errors,
		hasStrategyGroup,
		hasCategoryGroup,
		canDemandForecastNeeded,
		needCurrentProduct,
		// question,
		// onChangeQuestion,
		// loginAccount,
	} = props;
	const isRetest = !!job.retestJobId;
	const jobColumns = React.useMemo<Array<Column | React.ReactElement>[]>(() => {
		const shouldShowCurrentProductForms =
			(job.isDemandForecastNeeded ?? false) &&
			(job.hasCurrentProduct ||
				(isRetest &&
					job.currentProductPurchaseRate != null &&
					job.currentProductPurchaseRateReason != null &&
					job.currentProductRecognitionRate != null));
		const defaultcolumns: Column[][] = [
			[
				{
					type: "radios",
					name: "hasTwoTest",
					label: "自社テスト品",
					choices: [
						{ value: false, label: "1案" },
						{ value: true, label: "2案" },
					],
					col: 5,
				},
			],
		];
		if (canDemandForecastNeeded) {
			defaultcolumns.push([
				{
					type: "radios",
					name: "isDemandForecastNeeded",
					label: "需要予測の有無",
					disabled: isRetest,
					choices: [
						{ value: false, label: "なし" },
						{ value: true, label: "あり" },
					],
					col: 5,
				},
			]);
		}
		// 需要予測ありであれば表示
		if (shouldShowCurrentProductForms) {
			defaultcolumns.push(
				[
					{
						type: "number",
						name: "currentProductRecognitionRate",
						disabled: isRetest,
						label: (
							<ItemLabel>
								現行品/類似品の
								<br />
								日本人（該当性の）15～69歳における
								<br />
								商品認知率（％）
							</ItemLabel>
						),
						col: 6,
					},
					{
						type: "label",
						name: "currentProductRecognitionRat__hosoku",
						label: <AlertText>※HP Trackingやコミュニケーションカルテ結果などを参照</AlertText>,
					},
				],
				[
					{
						type: "decimal",
						step: "0.01",
						min: 0.01,
						name: "currentProductPurchaseRate",
						disabled: isRetest,
						label: (
							<ItemLabel>
								現行品/類似品の
								<br />
								直近12か月の購入率（％）
							</ItemLabel>
						),
						col: 6,
					},
					{
						type: "label",
						name: "currentProductPurchaseRate__hosoku",
						label: <PurchaseRateExtractionDefinitionNotes assessmentType="concept" />,
					},
				],
				[
					{
						type: "text",
						disabled: isRetest,
						name: "currentProductPurchaseRateReason",
						label: <ItemLabel>購入率（%）の定義（対象期間など）を記載</ItemLabel>,
						col: 6,
					},
				],
			);
		}
		defaultcolumns.push([
			{
				type: "datetimepicker",
				name: "startDatetime",
				label: (
					<ItemLabel>
						調査開始日時 <AlertMessage>※ 調査開始から4日後が最長の終了予定日となります。</AlertMessage>
					</ItemLabel>
				),
				col: 6,
			},
		]);
		const strategySettings: Column[][] = [
			[
				{
					type: "radios",
					name: "hasStrategicTarget",
					choices: [
						{ value: true, label: "回収する" },
						{ value: false, label: "回収しない" },
					],
					label: "戦略ターゲットグループ",
				},
			],
		];
		const otherConceptSettings: Array<Column | React.ReactElement>[] = [
			[
				{
					type: "label",
					label: "ベンチマーク",
					name: "head",
					col: 6,
				},
			],
			[
				<FormGroup className="col-hasCurrentProduct" as={Col} key="hasCurrentProduct">
					<FormLabel>
						現行品/類似品
						<AlertMessage>
							※現行品/類似品について1回目調査で実施した場合、再調査せず、1回目調査結果との比較となります。
						</AlertMessage>
					</FormLabel>
					<FormGroup as={Col} className="position-relative w-100 px-3">
						<FormCheck
							disabled={isRetest}
							type={"radio"}
							label="あり（現行品）"
							value="currentProduct"
							checked={job.hasCurrentProduct && !job.hasSimilarProduct}
							onChange={(e) => onChangeJob("hasCurrentProduct", e.target.checked)}
							name="hasCurrentProduct"
						/>
						<FormCheck
							disabled={isRetest}
							type={"radio"}
							label="あり（類似品）"
							value="similarProduct"
							checked={job.hasCurrentProduct && job.hasSimilarProduct}
							onChange={(e) => onChangeJob("hasSimilarProduct", e.target.checked)}
							name="hasCurrentProduct"
						/>
						<FormCheck
							disabled={needCurrentProduct || isRetest}
							type={"radio"}
							label="なし"
							value="false"
							checked={job.hasCurrentProduct != null ? !job.hasCurrentProduct : false}
							onChange={(_) => onChangeJob("hasCurrentProduct", false)}
							name="hasCurrentProduct"
						/>
					</FormGroup>
				</FormGroup>,
			],
			[
				{
					type: "radios",
					name: "hasBenchmark",
					disabled: isRetest,
					label: "競合品",
					choices: [
						{ value: true, label: "あり" },
						{ value: false, label: "なし" },
					],
					col: 5,
				},
				{
					type: "text",
					disabled: isRetest,
					name: "benchmarkName",
					label: (
						<>
							競合品ありの場合はブランド名と商品名を入力してください
							<br />
							（レポートのみに反映されます）
						</>
					),
					isShow: (data: Job) => {
						return data.hasBenchmark;
					},
				},
			],
		];
		const tmp = [];
		if (hasStrategyGroup && job.retestJobId) tmp.push(...strategySettings);
		if (hasCategoryGroup || hasStrategyGroup) tmp.push(...otherConceptSettings);
		tmp.push(...defaultcolumns);
		return tmp;
	}, [
		job.isDemandForecastNeeded,
		job.hasCurrentProduct,
		job.currentProductPurchaseRate,
		job.currentProductPurchaseRateReason,
		job.currentProductRecognitionRate,
		job.hasSimilarProduct,
		job.retestJobId,
		isRetest,
		canDemandForecastNeeded,
		needCurrentProduct,
		hasStrategyGroup,
		hasCategoryGroup,
		onChangeJob,
	]);
	const currentProductAlertModalDisclosure = useDisclosure();
	const handleSubmit = () => {
		// 需要予測あり＆現行品設定なし、で設定されたケースが発生した場合は、
		// システム側で「現行品が設定されていないため、現行品実績による補整は需要予測に反映されません。現行品は設定しなくてよいですか？」（アラート文は仮）
		// といったポップアップを入れるなど確認ステップを挟む
		if (!isRetest && job.isDemandForecastNeeded && job.hasCurrentProduct != null && !job.hasCurrentProduct) {
			currentProductAlertModalDisclosure.open();
		} else {
			onSubmit();
		}
	};

	return (
		<>
			<CurrentProductAlertModal
				onClose={currentProductAlertModalDisclosure.close}
				onSubmit={onSubmit}
				show={currentProductAlertModalDisclosure.isOpen}
			/>
			{!!cantEditMessage && (
				<div className="m-3">
					<Alert variant={"danger"}>{cantEditMessage}</Alert>
				</div>
			)}
			<div className="m-3">
				<div className="h3">■{job._id ? "ジョブ更新" : job.retestJobId ? "再調査登録" : "ジョブ新規登録"}</div>
			</div>
			<div className={"m-3"}>
				<Row>
					<Col md={12}>
						<Title>基本設定</Title>
					</Col>
				</Row>
				<IndentArea>
					<Row>
						<Col md={8}>
							<Row>
								<Col md={12}>
									<WrapArea>
										<FormInputGroups
											columns={jobColumns}
											onChange={onChangeJob}
											data={job}
											errors={errors && errors["job"] && typeof errors["job"] === "object" ? errors["job"] : undefined}
										/>
									</WrapArea>
								</Col>
							</Row>
						</Col>
					</Row>
				</IndentArea>
				<Row>
					<Col md={12}>
						<Title>カスタム設定</Title>
					</Col>
				</Row>
				<IndentArea>
					<Row>
						<Col md={12}>
							<FormGroup style={{ paddingBottom: "10px", borderBottom: "1px solid #ddd" }}>
								<FormLabel>印象項目設定</FormLabel>
								<Row>
									<Col md={4}>
										<div>
											標準項目 <AlertMessage>※変更や削除はできません</AlertMessage>
										</div>
										<List
											list={[
												"新しさを感じる",
												"他の商品とは違っている",
												"自分向けの商品だと思う",
												"効果が期待できそう",
												"自分の魅力を高めてくれそう",
												"自分の悩みや不安を解消してくれそう",
												"使うと気分が上がりそう",
												"商品の特徴がわかりやすい",
												"このブランドの他の商品も使ってみたくなる",
												"信じられる",
												"価格が高すぎる",
												"買い求めやすい",
											]}
										/>
									</Col>
									<Col md={8}>
										<div>
											追加項目（任意/5つまで）<AlertMessage>文字数を30文字以内としてください</AlertMessage>
										</div>
										<MultipleText
											max={5}
											hasAddEvent
											values={evaluationItem}
											onChange={onChangeEvaluationItem}
											sentenceOptions={{ max: 30 }}
										/>
										{errors && "evaluationItem" in errors && (
											<Feedback type={"invalid"} style={{ display: "block" }}>
												{getErrorMessage("evaluationItem", errors)}
											</Feedback>
										)}
									</Col>
								</Row>
							</FormGroup>
						</Col>
					</Row>
				</IndentArea>
				{/* 追加設問は別ページにて
				Role.hasManageSystemRole(loginAccount) && (
					<IndentArea>
						<Row>
							<Col md={12}>
								<FormGroup style={{ paddingBottom: "10px", borderBottom: "1px solid #ddd" }}>
									<FormLabel>追加設問</FormLabel>
									<Col md={12}>
										<EditQuestion question={question} mapQuestions={{}} onSave={onChangeQuestion} />
									</Col>
								</FormGroup>
							</Col>
						</Row>
					</IndentArea>
				)*/}
				<Row>
					<Col md={{ span: 2, offset: 4 }}>
						{!cantEditMessage ? (
							<Button className="w-100" variant="secondary" onClick={handleSubmit}>
								{job._id ? "更新" : "登録"}
							</Button>
						) : (
							<Button className="w-100" variant="secondary" disabled>
								{job._id ? "更新" : "登録"}できません
							</Button>
						)}
					</Col>
				</Row>
			</div>
		</>
	);
});
